import { navigate } from "../router";
import { el, mount, header, showToast } from "../ui";
import { sendBooking } from "../bot-bridge";
import { getProfile } from "../liff-init";
import {
  type BookingDraft,
  type SlotKey,
  type AccessNeed,
  SLOT_LABEL,
  NEED_LABEL,
} from "../types";

const today = (): string => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const tomorrow = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const draft: BookingDraft = {
  date: tomorrow(),
  slot: null,
  headcount: 2,
  needs: [],
  contactName: "",
  notes: "",
};

export function renderBooking(): void {
  // Reset slot when re-entering
  draft.slot = draft.slot;

  const profile = getProfile();
  if (profile?.displayName && !draft.contactName) draft.contactName = profile.displayName;

  const head = header({
    title: "預約導覽",
    subtitle: "藥草解說 · 園區巡禮 · 約 2.5 小時",
    back: () => navigate("home"),
  });

  // Date ----------------------------------------------------
  const dateInput = el("input", {
    type: "date",
    value: draft.date,
    min: today(),
  }) as HTMLInputElement;
  dateInput.addEventListener("change", () => {
    draft.date = dateInput.value;
    refreshSubmit();
  });
  const dateField = el("div", { class: "field" }, [
    el("label", {}, ["想哪一天來?"]),
    el("div", { class: "hint" }, ["最早可預約明天,熱門時段建議提前 3 天"]),
    dateInput,
  ]);

  // Slot ----------------------------------------------------
  const slotMorning = el("button", {
    type: "button",
    class: "slot" + (draft.slot === "morning" ? " selected" : ""),
  }, ["☀️ ", SLOT_LABEL.morning]);
  const slotAfternoon = el("button", {
    type: "button",
    class: "slot" + (draft.slot === "afternoon" ? " selected" : ""),
  }, ["🌤 ", SLOT_LABEL.afternoon]);
  const pickSlot = (key: SlotKey) => {
    draft.slot = key;
    slotMorning.classList.toggle("selected", key === "morning");
    slotAfternoon.classList.toggle("selected", key === "afternoon");
    refreshSubmit();
  };
  slotMorning.addEventListener("click", () => pickSlot("morning"));
  slotAfternoon.addEventListener("click", () => pickSlot("afternoon"));
  const slotField = el("div", { class: "field" }, [
    el("label", {}, ["想哪個時段?"]),
    el("div", { class: "slot-row" }, [slotMorning, slotAfternoon]),
  ]);

  // Headcount -----------------------------------------------
  const minus = el("button", { type: "button" }, ["−"]) as HTMLButtonElement;
  const plus = el("button", { type: "button" }, ["+"]) as HTMLButtonElement;
  const value = el("div", { class: "value" }, [`${draft.headcount} 人`]);
  const setHeadcount = (n: number) => {
    draft.headcount = Math.max(1, Math.min(50, n));
    value.textContent = `${draft.headcount} 人`;
    minus.disabled = draft.headcount <= 1;
    plus.disabled = draft.headcount >= 50;
  };
  minus.addEventListener("click", () => setHeadcount(draft.headcount - 1));
  plus.addEventListener("click", () => setHeadcount(draft.headcount + 1));
  setHeadcount(draft.headcount);
  const headcountField = el("div", { class: "field" }, [
    el("label", {}, ["共幾人?"]),
    el("div", { class: "hint" }, ["20 人以上享團體 8 折"]),
    el("div", { class: "counter" }, [minus, value, plus]),
  ]);

  // Needs ---------------------------------------------------
  const needsGroup = el("div", { class: "checkbox-group" });
  for (const [key, info] of Object.entries(NEED_LABEL) as [AccessNeed, typeof NEED_LABEL[AccessNeed]][]) {
    const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
    cb.checked = draft.needs.includes(key);
    const wrap = el("label", {
      class: "check" + (cb.checked ? " checked" : ""),
    }, [
      cb,
      el("div", {}, [
        el("div", { class: "label" }, [info.label]),
        el("div", { class: "sub" }, [info.sub]),
      ]),
    ]);
    cb.addEventListener("change", () => {
      if (cb.checked) draft.needs = [...new Set([...draft.needs, key])];
      else draft.needs = draft.needs.filter((n) => n !== key);
      wrap.classList.toggle("checked", cb.checked);
    });
    needsGroup.appendChild(wrap);
  }
  const needsField = el("div", { class: "field" }, [
    el("label", {}, ["有什麼想讓我們知道的嗎?"]),
    el("div", { class: "hint" }, ["可複選,我們會幫您準備"]),
    needsGroup,
  ]);

  // Contact + notes ----------------------------------------
  const nameInput = el("input", {
    type: "text",
    value: draft.contactName,
    placeholder: "您怎麼稱呼?",
    maxlength: "30",
  }) as HTMLInputElement;
  nameInput.addEventListener("input", () => {
    draft.contactName = nameInput.value.trim();
    refreshSubmit();
  });
  const nameField = el("div", { class: "field" }, [
    el("label", {}, ["您的稱呼"]),
    nameInput,
  ]);

  const notesInput = el("textarea", {
    rows: "3",
    placeholder: "其他需求(選填)",
    maxlength: "200",
  }) as HTMLTextAreaElement;
  notesInput.value = draft.notes;
  notesInput.addEventListener("input", () => { draft.notes = notesInput.value; });
  const notesField = el("div", { class: "field" }, [
    el("label", {}, ["備註"]),
    el("div", { class: "hint" }, ["例如:孩子年齡、想看的藥草、特殊飲食"]),
    notesInput,
  ]);

  // Submit --------------------------------------------------
  const submit = el("button", {
    type: "button",
    class: "btn btn-primary",
  }, ["送出預約"]) as HTMLButtonElement;

  const refreshSubmit = () => {
    submit.disabled = !draft.slot || !draft.contactName || !draft.date;
  };
  refreshSubmit();

  submit.addEventListener("click", () => {
    if (!draft.slot || !draft.contactName) return;
    showConfirm({ ...draft, slot: draft.slot });
  });

  const cancel = el("button", { type: "button", class: "btn btn-secondary" }, ["取消"]);
  cancel.addEventListener("click", () => navigate("home"));

  const actions = el("div", { class: "actions" }, [cancel, submit]);

  const form = el("section", { class: "form" }, [
    dateField,
    slotField,
    headcountField,
    needsField,
    nameField,
    notesField,
    actions,
  ]);

  mount(head, form);
}

function showConfirm(d: BookingDraft & { slot: SlotKey }): void {
  const summary = el("dl", { class: "summary" }, [
    el("dt", {}, ["日期"]),     el("dd", {}, [d.date]),
    el("dt", {}, ["時段"]),     el("dd", {}, [SLOT_LABEL[d.slot]]),
    el("dt", {}, ["人數"]),     el("dd", {}, [`${d.headcount} 人`]),
    el("dt", {}, ["稱呼"]),     el("dd", {}, [d.contactName]),
  ]);
  if (d.needs.length) {
    summary.appendChild(el("dt", {}, ["特別需求"]));
    summary.appendChild(el("dd", {}, [d.needs.map((n) => NEED_LABEL[n].label).join("、")]));
  }
  if (d.notes) {
    summary.appendChild(el("dt", {}, ["備註"]));
    summary.appendChild(el("dd", {}, [d.notes]));
  }

  const cancelBtn = el("button", { class: "btn btn-secondary" }, ["再修改"]);
  const confirmBtn = el("button", { class: "btn btn-primary" }, ["確認送出"]) as HTMLButtonElement;

  const back = el("div", { class: "modal-back" });
  const modal = el("div", { class: "modal" }, [
    el("h3", {}, ["再次確認"]),
    summary,
    el("div", { class: "actions" }, [cancelBtn, confirmBtn]),
  ]);
  back.appendChild(modal);
  document.body.appendChild(back);

  cancelBtn.addEventListener("click", () => back.remove());
  confirmBtn.addEventListener("click", async () => {
    confirmBtn.disabled = true;
    confirmBtn.textContent = "送出中…";
    try {
      await sendBooking(d);
      back.remove();
      showToast("預約已送出 ✨ 客服小幫手會跟您確認");
      // Reset draft & go home
      draft.slot = null;
      draft.notes = "";
      navigate("home");
    } catch (err) {
      confirmBtn.disabled = false;
      confirmBtn.textContent = "確認送出";
      showToast(`送出失敗:${(err as Error).message}`, "error");
    }
  });
}
